import { useMemo, useState } from "react";
import { ScrollView, useWindowDimensions, View } from "react-native";
import Svg, { Circle, G, Line, Rect, Text as SvgText } from "react-native-svg";
import { ChartColumn, ChartScatter, Table2 } from "lucide-react-native";
import {
  CardTitleText,
  MetaText,
  MoonFilterChip,
  MoonSegmentedControl,
  Row,
  SectionTitleRow,
  StatusBadge,
  SupportingSubtitleText,
} from "@louise/moon-ui";
import {
  EMBRYO_RESULT_LABEL_KEYS,
  EMBRYO_RESULT_TONES,
  getPolygenicRiskConditions,
} from "@src/packages/care/cryovault/embryo-detail";
import {
  layoutRiskColumns,
  polygenicRiskScaleMaximum,
  readRiskPercentage,
  readRiskPercentile,
  relativeRiskDifferencePercentage,
} from "@src/packages/care/cryovault/embryo-detail/embryoPolygenicRisk";
import type {
  EmbryoGenomicResult,
  EmbryoPolygenicRisk,
} from "@src/packages/cycles/hooks/useEmbryoReport";
import { OrganicMiniIcon } from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import { useLocale, useTranslations } from "@src/packages/i18n";
import { typography } from "@louise/theme/tokens";

// Plot marks and axes only; surrounding labels use canonical typography.
const COMPARISON_COLORS = {
  ink: "#303437",
  secondary: "#73777A",
  muted: "#B6B9BB",
} as const;

export type EmbryoChoice = Pick<
  EmbryoGenomicResult,
  "id" | "result" | "polygenicRisks"
> & { label: string };

type ComparisonView = "risk" | "percentile" | "table";

/** Every embryo for one disease, on one plot rather than separate row tracks. */
function SharedRiskPlot({
  compared,
  condition,
  currentEmbryoId,
  maximum,
  kind,
}: {
  compared: { choice: EmbryoChoice; risk: EmbryoPolygenicRisk | null }[];
  condition: string;
  currentEmbryoId: string;
  maximum: number;
  kind: "risk" | "percentile";
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  const [width, setWidth] = useState(264);
  const values = compared.map(({ risk }) =>
    risk
      ? kind === "risk"
        ? readRiskPercentage(risk.absoluteRisk)
        : readRiskPercentile(risk.percentile)
      : null,
  );
  const chartWidth = Math.max(width, 48 + compared.length * 56);
  const markers = layoutRiskColumns(values, maximum, chartWidth).flatMap(
    (marker) => {
      const entry = compared[marker.index];
      return entry ? [{ ...marker, choice: entry.choice }] : [];
    },
  );
  const references =
    kind === "percentile"
      ? [50]
      : [
          ...new Set(
            compared.flatMap(({ risk }) => {
              const value = risk
                ? readRiskPercentage(risk.populationRisk)
                : null;
              return value === null ? [] : [value];
            }),
          ),
        ];
  const number = (value: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 6 }).format(value);
  const formatValue = (value: number) =>
    kind === "risk"
      ? new Intl.NumberFormat(locale, {
          style: "percent",
          maximumFractionDigits: 6,
        }).format(value / 100)
      : number(value);
  const metric = t(
    kind === "risk"
      ? "care.embryo_detail.lifetime_risk"
      : "care.embryo_detail.risk_percentile",
  );
  const referenceLabel =
    kind === "percentile"
      ? t("care.embryo_detail.population_median_percentile")
      : t(
          references.length > 1
            ? "care.embryo_detail.reported_population_averages"
            : "care.embryo_detail.population_average",
        );
  const referenceSummary =
    kind === "percentile"
      ? referenceLabel
      : `${referenceLabel}: ${references.length ? references.map(formatValue).join(", ") : t("care.embryo_detail.no_polygenic_score_reported")}`;
  const summary = t("care.embryo_detail.embryos_on_shared_risk_plot", {
    params: {
      condition,
      metric,
      values: markers
        .map(({ choice, value }) => `${choice.label}: ${formatValue(value)}`)
        .join("; "),
      reference: referenceSummary,
    },
  });
  const plotTop = 26;
  const plotBottom = 170;
  const columnWidth = (chartWidth - 48) / compared.length;
  const barWidth = Math.min(42, columnWidth * 0.45);
  const yFor = (value: number) =>
    plotBottom - (value / maximum) * (plotBottom - plotTop);
  const ticks = [0, maximum / 2, maximum];

  if (!markers.length) {
    return (
      <MetaText>
        {t("care.embryo_detail.no_numeric_risk_positions_reported")}
      </MetaText>
    );
  }

  return (
    <View
      style={{ gap: 12, paddingTop: 12, paddingBottom: 4 }}
      onLayout={(event) => {
        const measuredWidth = event.nativeEvent.layout.width;
        if (measuredWidth >= 48) {
          setWidth(measuredWidth);
        }
      }}
    >
      <SupportingSubtitleText>{metric}</SupportingSubtitleText>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        scrollEnabled={chartWidth > width}
      >
        <View
          testID="embryo-shared-risk-plot"
          accessible
          accessibilityRole="image"
          accessibilityLabel={summary}
          style={{ width: chartWidth }}
        >
          <Svg width={chartWidth} height={184}>
            {ticks.map((tick) => (
              <G key={tick}>
                <Line
                  x1={40}
                  x2={chartWidth - 8}
                  y1={yFor(tick)}
                  y2={yFor(tick)}
                  stroke={COMPARISON_COLORS.muted}
                  strokeOpacity={0.28}
                  strokeWidth={0.5}
                />
                <SvgText
                  x={30}
                  y={yFor(tick) + 4}
                  textAnchor="end"
                  fill={COMPARISON_COLORS.secondary}
                  fontSize={10}
                  fontFamily={typography.families.sans}
                >
                  {formatValue(tick)}
                </SvgText>
              </G>
            ))}
            {markers.map(({ choice, x, value }) => {
              const selected = choice.id === currentEmbryoId;
              const y = yFor(value);
              return (
                <G key={choice.id}>
                  {kind === "risk" ? (
                    <Rect
                      testID={`embryo-risk-bar-${choice.id}`}
                      x={x - barWidth / 2}
                      y={y}
                      width={barWidth}
                      height={plotBottom - y}
                      rx={3}
                      fill={
                        selected
                          ? COMPARISON_COLORS.ink
                          : COMPARISON_COLORS.muted
                      }
                      fillOpacity={selected ? 1 : 0.55}
                    />
                  ) : (
                    <Circle
                      testID={`embryo-percentile-point-${choice.id}`}
                      cx={x}
                      cy={y}
                      r={5}
                      fill={
                        selected
                          ? COMPARISON_COLORS.ink
                          : COMPARISON_COLORS.secondary
                      }
                    />
                  )}
                </G>
              );
            })}
            {references.map((reference) => (
              <Line
                key={reference}
                testID={`embryo-population-reference-${reference}`}
                x1={40}
                x2={chartWidth - 8}
                y1={yFor(reference)}
                y2={yFor(reference)}
                stroke={COMPARISON_COLORS.secondary}
                strokeOpacity={0.55}
                strokeWidth={1}
                strokeDasharray="3,4"
              />
            ))}
            {markers.map(({ choice, x, value }) => (
              <SvgText
                key={choice.id}
                x={x}
                y={yFor(value) - 12}
                textAnchor="middle"
                fill={COMPARISON_COLORS.ink}
                fontSize={14}
                fontFamily={typography.families.sansMedium}
              >
                {formatValue(value)}
              </SvgText>
            ))}
          </Svg>
          <View
            style={{
              flexDirection: "row",
              paddingLeft: 40,
              paddingRight: 8,
            }}
          >
            {compared.map(({ choice }) => (
              <MetaText
                key={choice.id}
                numberOfLines={2}
                style={{
                  width: columnWidth,
                  textAlign: "center",
                  fontSize: 11,
                  lineHeight: 15,
                  paddingHorizontal: 3,
                  color:
                    choice.id === currentEmbryoId
                      ? COMPARISON_COLORS.ink
                      : COMPARISON_COLORS.secondary,
                }}
              >
                {choice.label}
              </MetaText>
            ))}
          </View>
        </View>
      </ScrollView>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <View
          style={{
            width: 16,
            borderTopWidth: 1,
            borderStyle: "dashed",
            borderColor: COMPARISON_COLORS.secondary,
          }}
        />
        <SupportingSubtitleText style={{ flexShrink: 1 }}>
          {referenceSummary}
        </SupportingSubtitleText>
      </View>
    </View>
  );
}

function ComparisonValues({ risk }: { risk: EmbryoPolygenicRisk }) {
  const t = useTranslations();
  const { locale } = useLocale();
  const percentile = readRiskPercentile(risk.percentile);
  return (
    <View
      style={{ flexDirection: "row", flexWrap: "wrap", gap: 8, marginTop: 7 }}
    >
      {[
        [t("care.embryo_detail.lifetime_risk"), risk.absoluteRisk],
        [t("care.embryo_detail.population_average"), risk.populationRisk],
        [
          t("care.embryo_detail.risk_percentile"),
          percentile === null
            ? "–"
            : new Intl.NumberFormat(locale).format(percentile),
        ],
      ].map(([label, value]) => (
        <View
          key={label}
          style={{ flexGrow: 1, flexBasis: "30%", minWidth: 60, gap: 3 }}
        >
          <MetaText>{label}</MetaText>
          <CardTitleText>{value}</CardTitleText>
        </View>
      ))}
    </View>
  );
}

export default function EmbryoPolygenicComparison({
  choices,
  currentEmbryoId,
  onSelectEmbryo,
}: {
  choices: EmbryoChoice[];
  currentEmbryoId: string;
  onSelectEmbryo?: (embryoId: string) => void;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  const { width } = useWindowDimensions();
  const conditions = useMemo(
    () => getPolygenicRiskConditions(choices),
    [choices],
  );
  const [selectedCondition, setSelectedCondition] = useState<string | null>(
    null,
  );
  const [view, setView] = useState<ComparisonView>("risk");
  const condition =
    conditions.find((candidate) => candidate.condition === selectedCondition) ??
    conditions[0];
  const compared = choices.map((choice) => ({
    choice,
    risk:
      choice.polygenicRisks.find(
        (candidate) => candidate.condition === condition?.condition,
      ) ?? null,
  }));
  const maximum = polygenicRiskScaleMaximum(
    compared.flatMap(({ risk }) => (risk ? [risk] : [])),
  );
  const number = (value: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 6 }).format(value);

  if (!condition || choices.length < 2) return null;

  return (
    <View
      testID="embryo-polygenic-comparison"
      style={{ gap: 12, marginTop: 24 }}
    >
      <SectionTitleRow
        title={t("care.embryo_detail.compare_risk_across_embryos")}
        icon={ChartColumn}
        style={{ paddingHorizontal: 0, marginBottom: 0 }}
        right={
          <MoonSegmentedControl
            active={view}
            onChange={setView}
            shape="rounded"
            iconOnly={width < 420}
            style={{ marginTop: 0, flexShrink: 0 }}
            tabs={[
              {
                value: "risk",
                label: t("care.embryo_detail.risk_chart"),
                icon: ChartColumn,
                action: "embryo_comparison_risk_chart",
              },
              {
                value: "percentile",
                label: t("care.embryo_detail.risk_percentile"),
                icon: ChartScatter,
                action: "embryo_comparison_percentile_chart",
              },
              {
                value: "table",
                label: t("care.embryo_detail.risk_table"),
                icon: Table2,
                action: "embryo_comparison_table",
              },
            ]}
          />
        }
      />
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ gap: 8, paddingRight: 16 }}
      >
        {conditions.map((candidate) => (
          <MoonFilterChip
            key={candidate.condition}
            label={candidate.condition}
            selected={candidate.condition === condition.condition}
            onPress={() => setSelectedCondition(candidate.condition)}
            tone="glass"
            action="select_polygenic_condition"
          />
        ))}
      </ScrollView>
      {view !== "table" && (
        <SharedRiskPlot
          compared={compared}
          condition={condition.condition}
          currentEmbryoId={currentEmbryoId}
          kind={view}
          maximum={view === "risk" ? maximum : 100}
        />
      )}
      <View style={{ gap: 8 }}>
        {compared.map(({ choice, risk }) => {
          const percentile = risk ? readRiskPercentile(risk.percentile) : null;
          const difference = risk
            ? relativeRiskDifferencePercentage(risk)
            : null;
          const comparisonText =
            difference === null
              ? null
              : difference === 0
                ? t("care.embryo_detail.at_population_average")
                : Math.abs(difference) < 1
                  ? t(
                      difference < 0
                        ? "care.embryo_detail.less_than_one_percent_below_average"
                        : "care.embryo_detail.less_than_one_percent_above_average",
                    )
                  : t(
                      difference < 0
                        ? "care.embryo_detail.relative_percentage_below_average"
                        : "care.embryo_detail.relative_percentage_above_average",
                      {
                        params: {
                          percentage: new Intl.NumberFormat(locale, {
                            style: "percent",
                            maximumFractionDigits: 0,
                          }).format(Math.abs(difference) / 100),
                        },
                      },
                    );
          const riskSummary = risk
            ? `${t("care.embryo_detail.lifetime_risk")}: ${risk.absoluteRisk}. ${t(
                "care.embryo_detail.population_average_risk",
                { params: { risk: risk.populationRisk } },
              )}. ${t("care.embryo_detail.risk_percentile")}: ${
                percentile === null ? "–" : number(percentile)
              }.`
            : t("care.embryo_detail.no_polygenic_score_reported");
          return (
            <Row
              key={choice.id}
              testID={`embryo-risk-row-${choice.id}`}
              elevated={false}
              leading={<OrganicMiniIcon kind="genetics" size={34} />}
              title={choice.label}
              titleRight={
                <StatusBadge
                  label={t(EMBRYO_RESULT_LABEL_KEYS[choice.result])}
                  tone={EMBRYO_RESULT_TONES[choice.result]}
                />
              }
              onPress={
                onSelectEmbryo ? () => onSelectEmbryo(choice.id) : undefined
              }
              accessibilityState={{ selected: choice.id === currentEmbryoId }}
              action="select_embryo_from_risk_comparison"
              accessibilityLabel={`${choice.label}. ${riskSummary}${
                view === "risk" && comparisonText ? ` ${comparisonText}.` : ""
              }`}
              trailing={
                risk && view !== "table" ? (
                  <CardTitleText>
                    {view === "risk"
                      ? risk.absoluteRisk
                      : percentile === null
                        ? "–"
                        : number(percentile)}
                  </CardTitleText>
                ) : undefined
              }
              subtitle={
                !risk ? (
                  <MetaText style={{ marginTop: 6 }}>{riskSummary}</MetaText>
                ) : view === "table" ? (
                  <ComparisonValues risk={risk} />
                ) : (
                  <MetaText style={{ marginTop: 5 }}>
                    {view === "risk"
                      ? (comparisonText ??
                        t("care.embryo_detail.population_average_risk", {
                          params: { risk: risk.populationRisk },
                        }))
                      : `${t("care.embryo_detail.lifetime_risk")}: ${risk.absoluteRisk}`}
                  </MetaText>
                )
              }
            />
          );
        })}
      </View>
    </View>
  );
}
